/**
 * A10 Off-Page Brief shared type contracts.
 *
 * Spec: docs/features/a10-off-page-brief.md §5.3 (Zod contracts) + §5.6 (API DTOs).
 *
 * These schemas are the load-bearing contract between:
 *   - workers (produce PlatformVariantOutput / CritiqueVariantOutput /
 *     DiscoveryResults / OutreachPitchOutput from LLM calls, validate before persist)
 *   - api (validates request bodies via *RequestSchema DTOs)
 *   - web  (consumes the wire shape)
 *
 * Enum schemas mirror the drizzle enums declared in §5.2 (see
 * `packages/db/src/schema/enums.ts`). Keep the two in sync — any enum
 * value drift silently breaks the API-layer validation.
 */
import { z } from 'zod';
import { UuidSchema } from './common.js';

// ─────────────────────────────────────────────────────────────────────────────
// Enum schemas — mirror the drizzle enums in packages/db/src/schema/enums.ts.
// Kept as top-level exports so both API DTOs and downstream row schemas can
// share the same source of truth.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Monthly Off-Page Brief allowance per plan (spec §6 pricing table).
 * `Infinity` = unlimited; `0` = the tier can't generate off-page briefs at all.
 *
 * Shared so EVERY creation path agrees. Previously this table lived only in
 * `apps/api/src/usecases/create-off-page-brief.ts`, so the manual button
 * enforced it while the auto-campaign orchestrator — which inserts
 * `off_page_briefs` rows straight through the repo — did not. A trial tenant
 * (cap 2) accumulated 9 auto-created briefs in a month and was then blocked
 * from manually retrying the one that failed, which is exactly backwards.
 */
export const MONTHLY_OPB_CAP: Record<string, number> = {
  trial: 2,
  audit: 0,
  content: 5,
  copilot: Number.POSITIVE_INFINITY,
};

/** Resolve a plan's monthly OPB cap; unknown plans get 0 (fail closed). */
export function monthlyOpbCap(plan: string | null | undefined): number {
  return MONTHLY_OPB_CAP[plan ?? ''] ?? 0;
}

export const OffPageBriefStatusSchema = z.enum([
  'queued',
  'planning',
  'critiquing',
  'grounding',
  'ready',
  'failed',
  'cancelled',
]);
export type OffPageBriefStatus = z.infer<typeof OffPageBriefStatusSchema>;

export const VariantStatusSchema = z.enum([
  'draft',
  'needs_review',
  'approved',
  'posted',
  'removed',
  'failed',
]);
export type VariantStatus = z.infer<typeof VariantStatusSchema>;

export const OffPageBriefPlatformSchema = z.enum([
  'reddit',
  'quora',
  'linkedin_post',
  'linkedin_article',
  'medium',
  'dev_to',
  'x_thread',
]);
export type OffPageBriefPlatform = z.infer<typeof OffPageBriefPlatformSchema>;

export const VariantModeSchema = z.enum(['fresh_post', 'comment_on_thread']);
export type VariantMode = z.infer<typeof VariantModeSchema>;

export const LinkPlacementSchema = z.enum(['body', 'comment', 'bio_only', 'none']);
export type LinkPlacement = z.infer<typeof LinkPlacementSchema>;

export const AccountReadinessSchema = z.enum([
  'not_connected',
  'warming_up',
  'blocked',
  'ready',
]);
export type AccountReadiness = z.infer<typeof AccountReadinessSchema>;

export const OutreachPitchTypeSchema = z.enum([
  'connectively',
  'qwoted',
  'featured',
  'journalist',
  'broken_link',
  'guest_post',
]);
export type OutreachPitchType = z.infer<typeof OutreachPitchTypeSchema>;

export const OutreachPitchStatusSchema = z.enum([
  'draft',
  'sent',
  'replied',
  'landed',
  'declined',
  'ghosted',
]);
export type OutreachPitchStatus = z.infer<typeof OutreachPitchStatusSchema>;

export const OutcomesProbeSchema = z.enum([
  'backlink',
  'ai_citation',
  'referral',
  'brand_mention',
]);
export type OutcomesProbe = z.infer<typeof OutcomesProbeSchema>;

export const OutcomesSourceKindSchema = z.enum(['variant', 'pitch', 'brief']);
export type OutcomesSourceKind = z.infer<typeof OutcomesSourceKindSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// LLM output schemas — validated against every plan-variants / critique-variants
// / outreach-pitch response. See §5.3.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PlatformVariantOutputSchema — output of the `plan-variants` fan-out step.
 * One instance per (platform, mode, hook_variant) tuple emitted by the LLM.
 * Downstream: the API persists this into `off_page_brief_variants`, splitting
 * hook_variant_a/b into two rows.
 */
export const PlatformVariantOutputSchema = z.object({
  platform: z.enum([
    'reddit',
    'quora',
    'linkedin_post',
    'linkedin_article',
    'medium',
    'dev_to',
    'x_thread',
  ]),
  mode: z.enum(['fresh_post', 'comment_on_thread']),
  format: z.enum([
    'self_post',
    'answer',
    'comment',
    'feed_post',
    'article',
    'thread',
    'long_form',
  ]),
  title: z.string().nullable(),
  body: z.string().min(50).max(20000),
  hook_variant_a: z.string().min(10).max(500),
  hook_variant_b: z.string().min(10).max(500),
  suggested_target: z.object({
    subreddit: z.string().optional(),
    thread_url: z.string().url().optional(),
    question_url: z.string().url().optional(),
    hashtags: z.array(z.string()).optional(),
  }),
  link_placement: z.enum(['body', 'comment', 'bio_only', 'none']),
  evidence_ids: z.array(z.string()),
  authenticity_signals: z.array(z.string()),
  risk_flags: z.array(z.string()),
  content_hash_input: z.string(),
});
export type PlatformVariantOutput = z.infer<typeof PlatformVariantOutputSchema>;

/**
 * CritiqueVariantOutputSchema — output of the `critique-variants` step.
 * The critique score + notes are persisted onto the variant row; blocking
 * notes flip status to `needs_review`.
 */
export const CritiqueVariantOutputSchema = z.object({
  variant_id: z.string().uuid(),
  score: z.number().int().min(0).max(100),
  scores_breakdown: z.object({
    authenticity: z.number().int().min(0).max(100),
    platform_native_tone: z.number().int().min(0).max(100),
    promotional_signal_risk: z.number().int().min(0).max(100),
    evidence_density: z.number().int().min(0).max(100),
    spam_detection_risk: z.number().int().min(0).max(100),
  }),
  notes: z.array(
    z.object({
      severity: z.enum(['info', 'warn', 'block']),
      category: z.enum([
        'banned_phrase',
        'subreddit_rule',
        'evidence_missing',
        'promotional',
        'duplicate',
        'other',
      ]),
      message: z.string(),
      location: z.string().optional(),
    }),
  ),
});
export type CritiqueVariantOutput = z.infer<typeof CritiqueVariantOutputSchema>;

/**
 * OutreachPitchOutputSchema — Phase 2 outreach workflow output.
 * One instance per drafted pitch to a HARO-successor query or journalist target.
 */
export const OutreachPitchOutputSchema = z.object({
  target: z.object({
    query_id: z.string().optional(),
    contact_email: z.string().email().optional(),
    publication: z.string().optional(),
    reporter_name: z.string().optional(),
    deadline: z.string().datetime().optional(),
  }),
  subject_line: z.string().min(10).max(120),
  pitch_body: z.string().min(300).max(1200),
  evidence_ids: z.array(z.string()),
  personalization_notes: z.array(z.string()),
});
export type OutreachPitchOutput = z.infer<typeof OutreachPitchOutputSchema>;

/**
 * DiscoveryResultsSchema — persisted in `off_page_briefs.discovery_results`.
 * Per-platform target discovery output from the grounding step. Each
 * top-level key is nullable because discovery may skip a platform when
 * data sources are unavailable or the tenant didn't request it.
 */
export const DiscoveryResultsSchema = z.object({
  reddit: z
    .object({
      fresh_post_targets: z.array(
        z.object({
          subreddit: z.string(),
          subscribers: z.number().int(),
          score: z.number(),
          rules_summary: z.string().optional(),
          why: z.string(),
        }),
      ),
      comment_targets: z.array(
        z.object({
          thread_url: z.string().url(),
          thread_title: z.string(),
          subreddit: z.string(),
          comment_count: z.number().int(),
          score: z.number(),
          last_active_at: z.string().datetime(),
        }),
      ),
    })
    .nullable(),
  quora: z
    .object({
      fresh_post_targets: z.array(
        z.object({
          question_url: z.string().url(),
          question: z.string(),
          followers: z.number().int().optional(),
          answer_count: z.number().int().optional(),
          score: z.number(),
        }),
      ),
      comment_targets: z.array(z.unknown()).default([]),
    })
    .nullable(),
  linkedin: z
    .object({
      hashtags: z.array(z.string()),
    })
    .nullable(),
  x: z
    .object({
      hashtags: z.array(z.string()),
    })
    .nullable(),
});
export type DiscoveryResults = z.infer<typeof DiscoveryResultsSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// API DTOs — request-body schemas for the endpoints in §5.6.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * CreateOffPageBriefRequestSchema — body of
 * `POST /v1/briefs/:id/off-page-brief` (Panel A).
 * `platforms` omitted ⇒ API uses tenant default set. `regenerate: true`
 * bypasses the OFF_PAGE_BRIEF_ALREADY_EXISTS 409.
 */
export const CreateOffPageBriefRequestSchema = z.object({
  platforms: z.array(OffPageBriefPlatformSchema).optional(),
  regenerate: z.boolean().optional(),
});
export type CreateOffPageBriefRequest = z.infer<typeof CreateOffPageBriefRequestSchema>;

/**
 * CreateOffPageBriefFromUrlRequestSchema — body of
 * `POST /v1/projects/:id/off-page-briefs/from-url` (Phase 2, Panel B).
 * The API kicks off an A3 shadow-brief on the URL first to satisfy the
 * A3-backed invariant, then chains into the off-page workflow.
 */
export const CreateOffPageBriefFromUrlRequestSchema = z.object({
  source_article_url: z.string().url(),
});
export type CreateOffPageBriefFromUrlRequest = z.infer<
  typeof CreateOffPageBriefFromUrlRequestSchema
>;

/**
 * MarkVariantPostedRequestSchema — body of
 * `POST /v1/off-page-briefs/:id/variants/:variantId/mark-posted`.
 * The primary posting path (manual). API validates that the URL host matches
 * the platform's canonical host before persisting.
 */
export const MarkVariantPostedRequestSchema = z.object({
  posted_url: z.string().url(),
});
export type MarkVariantPostedRequest = z.infer<typeof MarkVariantPostedRequestSchema>;

/**
 * PatchVariantRequestSchema — body of
 * `PATCH /v1/off-page-briefs/:id/variants/:variantId`.
 * All fields optional; nulls not accepted — omit to leave unchanged.
 */
export const PatchVariantRequestSchema = z.object({
  title: z.string().optional(),
  body: z.string().optional(),
  target: z.unknown().optional(),
  hook_variant: z.string().optional(),
});
export type PatchVariantRequest = z.infer<typeof PatchVariantRequestSchema>;

/**
 * RegenerateOffPageBriefRequestSchema — body of
 * `POST /v1/off-page-briefs/:id/regenerate`. `platforms` omitted ⇒
 * regenerate all previously requested platforms.
 */
export const RegenerateOffPageBriefRequestSchema = z.object({
  platforms: z.array(OffPageBriefPlatformSchema).optional(),
});
export type RegenerateOffPageBriefRequest = z.infer<
  typeof RegenerateOffPageBriefRequestSchema
>;

/**
 * EnableAutopostRequestSchema — body of
 * `POST /v1/account-connections/:accountId/autopost/enable` (Phase 3).
 * `risk_ack` MUST be literal `true` — a bare boolean would let the UI
 * accidentally enable autopost without an explicit acknowledgment.
 */
export const EnableAutopostRequestSchema = z.object({
  account_id: UuidSchema,
  risk_ack: z.literal(true),
});
export type EnableAutopostRequest = z.infer<typeof EnableAutopostRequestSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Wire response shapes — what the API returns to the SPA. Kept separate from
// the LLM output / DTO schemas above so the two axes stay decoupled: the
// worker output contract can evolve without touching the client contract.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * OffPageBriefSchema — top-level row returned by `GET /v1/off-page-briefs`
 * (list) and embedded in `GET /v1/off-page-briefs/:id` (detail). Mirrors the
 * `off_page_briefs` row in §5.2 plus the derived brief-target-keyword field
 * the list endpoint denormalises for the sub-tab table.
 */
export const OffPageBriefSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  project_id: UuidSchema,
  brief_id: UuidSchema,
  status: OffPageBriefStatusSchema,
  platforms_requested: z.array(z.string()),
  content_version: z.number().int(),
  failure_reason: z.string().nullable().optional(),
  warnings: z.unknown().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type OffPageBrief = z.infer<typeof OffPageBriefSchema>;

/**
 * OffPageBriefVariantSchema — one variant row inside the detail response.
 * `autopostable` is computed by the API from `off_page_brief_accounts` state
 * (§5.6.1) so the UI can decide whether to render the Publish button
 * without a second query.
 */
export const OffPageBriefVariantSchema = z.object({
  id: UuidSchema,
  off_page_brief_id: UuidSchema,
  platform: OffPageBriefPlatformSchema,
  mode: VariantModeSchema,
  hook_variant: z.string(),
  format: z.string(),
  title: z.string().nullable(),
  body: z.string(),
  generation_prompt: z.string(),
  target: z.unknown(),
  link_placement: LinkPlacementSchema,
  canonical_url: z.string(),
  is_expected_nofollow: z.boolean(),
  critique_score: z.number().int(),
  critique_notes: z.unknown(),
  status: VariantStatusSchema,
  posted_url: z.string().nullable().optional(),
  posted_at: z.string().nullable().optional(),
  posted_by: z.string().nullable().optional(),
  autopostable: z.boolean(),
  evidence_ids: z.array(z.string()),
  warnings: z.unknown().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type OffPageBriefVariant = z.infer<typeof OffPageBriefVariantSchema>;

/**
 * OffPageBriefDetailSchema — the response body of
 * `GET /v1/off-page-briefs/:id`. Rows plus their variants.
 */
export const OffPageBriefDetailSchema = OffPageBriefSchema.extend({
  variants: z.array(OffPageBriefVariantSchema),
});
export type OffPageBriefDetail = z.infer<typeof OffPageBriefDetailSchema>;

/**
 * EligibleSourceBriefSchema — one row of
 * `GET /v1/off-page-briefs/eligible-source-briefs`.
 *
 * Powers the Panel A "Briefs" sub-tab. Rows are joined with an existing
 * off-page-brief (if any) so the UI can decide between "Generate" and
 * "Open" in a single query. `can_generate: false` rows still show up so
 * the tenant sees why the button is disabled — `disabled_reason` carries
 * the plain-English copy.
 */
export const EligibleSourceBriefSchema = z.object({
  brief_id: UuidSchema,
  project_id: UuidSchema,
  target_keyword: z.string(),
  brief_status: BriefStatusRefSchema(),
  off_page_brief_id: UuidSchema.nullable(),
  off_page_brief_status: OffPageBriefStatusSchema.nullable(),
  can_generate: z.boolean(),
  disabled_reason: z.string().nullable(),
});
export type EligibleSourceBrief = z.infer<typeof EligibleSourceBriefSchema>;

/**
 * Duplicate of the BriefStatus enum keys — declared inline so this file
 * does not have to import from ./brief.ts (which would create a
 * type-graph edge purely for one enum). Kept in sync manually with
 * `BriefStatusSchema` in ./brief.ts (verified by the API-level DTO test).
 */
function BriefStatusRefSchema() {
  return z.enum([
    'queued',
    'researching',
    'drafting',
    'delivering',
    'completed',
    'failed',
    'cancelled',
  ]);
}

/**
 * EligibleSourceBriefsResponseSchema — envelope for the sub-tab endpoint.
 */
export const EligibleSourceBriefsResponseSchema = z.object({
  items: z.array(EligibleSourceBriefSchema),
});
export type EligibleSourceBriefsResponse = z.infer<
  typeof EligibleSourceBriefsResponseSchema
>;

/**
 * OffPageBriefsListResponseSchema — envelope for `GET /v1/off-page-briefs`.
 */
export const OffPageBriefsListResponseSchema = z.object({
  items: z.array(OffPageBriefSchema),
});
export type OffPageBriefsListResponse = z.infer<
  typeof OffPageBriefsListResponseSchema
>;

/**
 * CreateOffPageBriefResponseSchema — response body of
 * `POST /v1/briefs/:id/off-page-brief`. Kicks off the workflow and returns
 * the newly-created OPB id + quota accounting for the tenant.
 */
export const CreateOffPageBriefResponseSchema = z.object({
  off_page_brief_id: UuidSchema,
  workflow_id: z.string(),
  quota_used: z.number().int(),
  quota_remaining: z.number().int(),
});
export type CreateOffPageBriefResponse = z.infer<
  typeof CreateOffPageBriefResponseSchema
>;
